import { buildPaginationHTML } from "../modules/archive-utils";

document.addEventListener("DOMContentLoaded", () => {
	// Inline video facade: click the poster → swap in the autoplay iframe (plays in place).
	document.querySelectorAll(".js-video-facade").forEach((facade) => {
		facade.addEventListener("click", () => {
			const embed = facade.dataset.embed;
			if (!embed || facade.classList.contains("is-playing")) return;

			const iframe = document.createElement("iframe");
			iframe.src = embed;
			iframe.setAttribute("frameborder", "0");
			iframe.setAttribute("allow", "autoplay; fullscreen; picture-in-picture; encrypted-media");
			iframe.setAttribute("allowfullscreen", "");

			facade.classList.add("is-playing");
			facade.innerHTML = "";
			facade.appendChild(iframe);
		});
	});

	// ---- On Demand (past webinars): local AJAX pagination, no URL change ----
	const onDemandGrid = document.getElementById("onDemandGrid");
	const onDemandPagination = document.getElementById("onDemandPagination");
	if (onDemandGrid && onDemandPagination && window.drAjax && window.drAjax.ajaxurl) {
		let currentPage = parseInt(onDemandPagination.dataset.current, 10) || 1;
		let totalPages = parseInt(onDemandPagination.dataset.pages, 10) || 1;

		const renderPagination = () => {
			onDemandPagination.innerHTML = buildPaginationHTML(currentPage, totalPages, {
				linkClass: "onDemand__pageLink",
				useArrows: false,
				prevText: "Back",
				nextText: "Next",
				range: 3,
			});
		};

		const loadPage = (page) => {
			const formData = new FormData();
			formData.append("action", "events_ondemand");
			formData.append("page", page);

			onDemandGrid.classList.add("is-loading");

			fetch(window.drAjax.ajaxurl, {
				method: "POST",
				body: formData,
				credentials: "same-origin",
			})
				.then((response) => response.json())
				.then((result) => {
					if (result && result.success && result.data) {
						onDemandGrid.innerHTML = result.data.html;
						currentPage = result.data.current_page;
						totalPages = result.data.pages;
						renderPagination();

						// Offset the scroll by the sticky header + Events nav so the
						// section top isn't hidden behind them.
						const header = document.querySelector(".siteHeader__navigation");
						const nav = document.querySelector(".eventsArchiveNav");
						const gap = 16;
						const offset =
							(header ? header.getBoundingClientRect().height : 0) +
							(nav ? nav.getBoundingClientRect().height : 0) +
							gap;
						const top = onDemandGrid.getBoundingClientRect().top + window.scrollY - offset;
						window.scrollTo({ top, behavior: "smooth" });
					}
				})
				.finally(() => {
					onDemandGrid.classList.remove("is-loading");
				});
		};

		onDemandPagination.addEventListener("click", (event) => {
			const link = event.target.closest("[data-page]");
			if (!link) return;
			event.preventDefault();
			const page = parseInt(link.dataset.page, 10);
			if (page && page !== currentPage) {
				loadPage(page);
			}
		});

		renderPagination();
	}

	const nav = document.querySelector(".eventsArchiveNav");
	if (!nav) {
		return;
	}

	const headerEl = document.querySelector(".siteHeader__navigation");

	const updateHeaderHeight = () => {
		if (!headerEl) return;
		const height = headerEl.getBoundingClientRect().height;
		if (height > 0) {
			document.documentElement.style.setProperty("--site-header-h", `${height}px`);
		}
	};

	updateHeaderHeight();
	window.addEventListener("resize", updateHeaderHeight);

	if (typeof ResizeObserver !== "undefined" && headerEl) {
		const resizeObserver = new ResizeObserver(updateHeaderHeight);
		resizeObserver.observe(headerEl);
	}

	// Toggle a compact state while the nav is stuck under the header.
	let stuckObserver = null;
	const setupStuckObserver = () => {
		if (typeof IntersectionObserver === "undefined") return;
		if (stuckObserver) stuckObserver.disconnect();
		const stickyTop = headerEl ? headerEl.getBoundingClientRect().height : 0;
		stuckObserver = new IntersectionObserver(
			([entry]) => {
				nav.classList.toggle("is-stuck", entry.intersectionRatio < 1);
			},
			{ rootMargin: `-${stickyTop + 1}px 0px 0px 0px`, threshold: [1] },
		);
		stuckObserver.observe(nav);
	};

	setupStuckObserver();
	window.addEventListener("resize", setupStuckObserver);

	const links = Array.from(nav.querySelectorAll(".eventsArchiveNav__link"));

	// Click → smooth scroll to the section, offset for the sticky header + nav.
	links.forEach((link) => {
		link.addEventListener("click", (event) => {
			const anchor = link.dataset.anchor;
			if (!anchor) return;
			const target = document.getElementById(anchor);
			if (!target) return;

			event.preventDefault();
			const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 0;
			const offset = headerHeight + nav.getBoundingClientRect().height;
			const top = target.getBoundingClientRect().top + window.scrollY - offset;
			window.scrollTo({ top, behavior: "smooth" });
		});
	});

	// ---- Events Nav scrollspy ----
	const sections = links
		.map((link) => {
			const anchor = link.dataset.anchor;
			if (!anchor) return null;
			const section = document.getElementById(anchor);
			if (!section) return null;
			return { anchor, link, section };
		})
		.filter(Boolean);

	if (sections.length === 0) {
		return;
	}

	const setActive = (anchor) => {
		links.forEach((link) => {
			link.classList.toggle("is-active", link.dataset.anchor === anchor);
		});
	};

	// Active section = the last one whose top has scrolled under the sticky nav.
	const updateActive = () => {
		const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 0;
		const line = headerHeight + nav.getBoundingClientRect().height + 1;

		let active = sections[0].anchor;
		for (const { anchor, section } of sections) {
			if (section.getBoundingClientRect().top - line <= 0) {
				active = anchor;
			} else {
				break;
			}
		}

		const atBottom =
			window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
		if (atBottom) {
			active = sections[sections.length - 1].anchor;
		}

		setActive(active);
	};

	let ticking = false;
	const onScroll = () => {
		if (ticking) return;
		ticking = true;
		window.requestAnimationFrame(() => {
			ticking = false;
			updateActive();
		});
	};

	window.addEventListener("scroll", onScroll, { passive: true });
	window.addEventListener("resize", onScroll);
	updateActive();
});
